<template>
  <div
    class="h-full min-h-0 shrink-0 flex flex-col border-r border-gray-200 bg-white text-gray-700 transition-[width] duration-200 dark:border-gray-800 dark:bg-gray-900 dark:text-gray-200"
    :class="collapsed ? 'w-14' : 'w-72'"
  >
    <div class="shrink-0 py-4" :class="collapsed ? 'px-2' : 'px-4'">
      <div class="flex items-center" :class="collapsed ? 'justify-center' : 'justify-between gap-2'">
        <h2 v-if="!collapsed" class="truncate text-xl font-semibold text-gray-900 dark:text-gray-100">
          {{ t(title) }}
        </h2>

        <UButton
          v-if="collapsible"
          icon="lucide:menu"
          color="neutral"
          variant="ghost"
          :aria-label="t('general.menu')"
          :title="t('general.menu')"
          @click="emit('toggleCollapsed')"
        />
      </div>
    </div>

    <OScroll class="grow shrink h-10">
      <nav class="pb-4" :aria-label="t(title)">
        <template v-for="item in menuItems" :key="item.path">
          <NuxtLink
            :to="item.to"
            class="group flex items-start border-t border-gray-200 py-3 text-left transition-colors first:border-t-0 dark:border-gray-800"
            :class="[
              collapsed ? 'justify-center px-2' : 'gap-3 px-4',
              item.active
                ? 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-300'
                : 'hover:bg-gray-50 dark:hover:bg-gray-800/60',
            ]"
            :aria-label="item.label"
            :title="collapsed ? item.label : undefined"
            @click="closeMobileMenu"
          >
            <UIcon
              v-if="item.icon?.startsWith('lucide:')"
              :name="item.icon"
              class="mt-0.5 size-4 shrink-0"
              :class="item.active ? 'text-primary-600 dark:text-primary-300' : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'"
            />
            <OIcon
              v-else-if="item.icon"
              :icon="item.icon"
              :size="16"
              class="mt-0.5 shrink-0"
              :class="item.active ? 'text-primary-600 dark:text-primary-300' : 'text-gray-400 group-hover:text-gray-600 dark:text-gray-500 dark:group-hover:text-gray-300'"
            />
            <span v-if="!collapsed" class="min-w-0">
              <span class="block text-sm font-medium leading-5 text-gray-900 dark:text-gray-100">
                {{ item.label }}
                <span v-if="item.defaultLabel" class="font-normal text-gray-400 dark:text-gray-500">
                  ({{ item.defaultLabel }})
                </span>
              </span>
              <span
                v-if="item.info"
                class="mt-0.5 block text-[13px] leading-[18px]"
                :class="item.active ? 'text-primary-700/80 dark:text-primary-200/80' : 'text-gray-500 dark:text-gray-400'"
              >
                {{ item.info }}
              </span>
            </span>
          </NuxtLink>
        </template>
      </nav>
    </OScroll>
  </div>
</template>

<script setup lang="ts">
import { OIcon, OScroll } from '@/components/elements'
import { settingsNavigationItemMatchesPath } from '@/lib/settingsSearch'

interface RawGroupConfig {
  label: string
  icon?: string
  items: any[]
}

interface Props {
  groups: RawGroupConfig[]
  title: string
  collapsed?: boolean
  collapsible?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  collapsed: false,
  collapsible: false,
})

const emit = defineEmits<{
  toggleCollapsed: []
}>()

const route = useRoute()
const { t } = useI18n()
const canAccess = useAccess()
const settingsMenuOpen = useState<boolean>('settings-menu-open', () => false)

interface RawItem {
  label: string
  icon?: string
  path: string
  aliases?: string[]
  info?: string
  /** Terminology catalog key (e.g. 'employee') this row's label renames. */
  term?: string
  children?: RawItem[]
}

interface MenuItem {
  label: string
  info?: string
  icon?: string
  path: string
  to: string
  active: boolean
  /** Original default word, shown subdued when the label has been renamed. */
  defaultLabel?: string
}

// Only fetched when at least one row declares a `term` (i.e. the Manage
// settings nav) on an account with the terminology gate enabled — the
// default/custom fields it needs aren't in the lean `auth.info.terminology`
// dictionary the rest of the app uses, and the management endpoint 403s for
// ungated accounts.
const { $gates } = useNuxtApp()
const needsTerminology = computed(() =>
  (($gates as any)?.get('terminology'))
  && props.groups.some((group) => group.items.some((item) => !!item.term)),
)
const { terminology, fetchTerminology } = useTerminologySettings()
onMounted(() => {
  if (needsTerminology.value) fetchTerminology()
})

const itemIsActive = (item: RawItem) => {
  const path = route.fullPath.replace(/#.*$/, '')
  return settingsNavigationItemMatchesPath(item, path)
    || !!item.children?.some((child) => settingsNavigationItemMatchesPath(child, path))
}

const firstVisiblePath = (item: RawItem) =>
  item.children?.find((child) => canAccess(child.path))?.path ?? item.path

const capitalize = (value: string) => (value ? value.charAt(0).toUpperCase() + value.slice(1) : value)

const defaultLabelFor = (item: RawItem): string | undefined => {
  if (!item.term) return undefined
  const term = terminology.value?.terms?.[item.term]
  return term?.custom ? capitalize(term.default.plural) : undefined
}

const toMenuItem = (item: RawItem): MenuItem | null => {
  const visibleChildren = item.children?.filter((c) => canAccess(c.path)) ?? []
  const hasChildren = visibleChildren.length > 0
  if (!canAccess(item.path) && !hasChildren) return null

  const primaryChild = visibleChildren[0]
  const label = t(item.label)
  const infoKey = item.info ?? primaryChild?.info
  const info = infoKey ? t(infoKey) : undefined

  return {
    label,
    info,
    icon: item.icon,
    path: item.path,
    to: firstVisiblePath(item),
    active: itemIsActive(item),
    defaultLabel: defaultLabelFor(item),
  }
}

const menuItems = computed<MenuItem[]>(() => {
  const result: MenuItem[] = []
  for (const group of props.groups) {
    for (const item of group.items) {
      const nav = toMenuItem(item)
      if (nav) result.push(nav)
    }
  }
  return result
})

const closeMobileMenu = () => {
  if (import.meta.client && window.innerWidth < 1024) {
    settingsMenuOpen.value = false
  }
}
</script>
